/**
 * The role catalogue, loaded once and shared by the screens that need it.
 *
 * `permission_keys` are the EFFECTIVE keys from the database, not the catalogue source,
 * so what an administrator is shown when they choose a role is what will actually be
 * enforced. That is the whole reason the API returns them at all.
 *
 * Disabled for a reader without `role.view`, so a screen that merely mentions roles
 * does not fire a request that can only be refused.
 */

import { STAFF, qk, usePaginatedList, type RoleDefinition } from '@legalcrm/api';
import { useMemo } from 'react';

import { useCan } from '@/auth/permissions';

export interface RoleCatalogue {
  roles: RoleDefinition[];
  /** Keyed by `role_key`, which is what the member payload and the API both use. */
  byKey: Map<string, RoleDefinition>;
  options: Array<{ value: string; label: string }>;
  canView: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

export function useRoleCatalogue(): RoleCatalogue {
  const canView = useCan('role', 'view');

  // Eleven roles in this phase, so one page is the whole catalogue.
  const list = usePaginatedList<RoleDefinition>(
    qk.list('roles'),
    STAFF.roles,
    { limit: 100 },
    { enabled: canView },
  );

  const byKey = useMemo(() => {
    const index = new Map<string, RoleDefinition>();
    for (const role of list.items) index.set(role.key, role);
    return index;
  }, [list.items]);

  return {
    roles: list.items,
    byKey,
    options: list.items.map((role) => ({ value: role.key, label: role.name })),
    canView,
    isLoading: list.isLoading,
    error: list.error,
    refetch: () => void list.refetch(),
  };
}
